import { jwtDecode } from "jwt-decode";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookie, useSearchParam } from "react-use";
import { ILocalUser, ILoginPayload, ISingUpPayload } from "../../@types";
import { APP_LINKS } from "../../database/app-link";
import COOKIE_KEYS from "../../database/cookie-keys";
import ERROR_MESSAGES from "../../database/error-messages";
import services from "../../services";
import { IAuthContext } from "./@types";

const AuthContext = React.createContext<IAuthContext>({
  user: null,
  error: null,
  loading: true,
} as IAuthContext);

function AuthContextProvider(props: React.HTMLAttributes<HTMLDivElement>) {
  const navigate = useNavigate();
  const location = useLocation();
  const nextUrl = useSearchParam("next");
  const appLinks = useMemo(() => APP_LINKS, []);
  const [cookieAuth, setCookieAuth, deleteCookieAuth] = useCookie(
    COOKIE_KEYS.auth
  );

  const [user, setUser] = React.useState<ILocalUser | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const userId = user?.id;

  async function login({ email, password }: ILoginPayload) {
    try {
      if (error) setError(null);
      setLoading(true);

      const serverUser = await services.auth.login({ email, password });

      if (!serverUser) throw new Error(ERROR_MESSAGES.serverError);

      const localUser = {} as ILocalUser;
      localUser.token = serverUser.token;
      localUser.email = serverUser.email;
      localUser.accessLevel = serverUser.accessLevel;
      localUser.id = serverUser.id;
      localUser.level = serverUser.level;
      localUser.name = serverUser.name;
      localUser.surname = serverUser.surname;
      localUser.type = serverUser.type;

      const cookieAuthExpiresIn = new Date();
      cookieAuthExpiresIn.setHours(cookieAuthExpiresIn.getHours() + 23);

      setCookieAuth(JSON.stringify(localUser), {
        expires: cookieAuthExpiresIn,
      });
      setUser(localUser);

      if (serverUser.type.toUpperCase() === "ADMIN") {
        const appLink = appLinks.find(({ roles }) =>
          roles.includes(serverUser.accessLevel)
        );
        if (appLink?.link)
          navigate(nextUrl ?? `${appLink.link}`, { replace: true });
      } else navigate(nextUrl ?? "/");
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function signUp({ name, email, password }: ISingUpPayload) {
    try {
      if (error) setError(null);
      setLoading(true);

      const serverUser = await services.auth.signUp({ name, email, password });

      if (!serverUser) throw new Error(ERROR_MESSAGES.serverError);

      const localUser = {} as ILocalUser;
      localUser.token = serverUser.token;
      localUser.email = serverUser.email;
      localUser.accessLevel = serverUser.accessLevel;
      localUser.id = serverUser.id;
      localUser.level = serverUser.level;
      localUser.name = serverUser.name;
      localUser.surname = serverUser.surname;
      localUser.type = serverUser.type;

      const cookieAuthExpiresIn = new Date();
      cookieAuthExpiresIn.setHours(cookieAuthExpiresIn.getHours() + 23);

      setCookieAuth(JSON.stringify(localUser), {
        expires: cookieAuthExpiresIn,
      });

      setUser(localUser);

      navigate(`/signup/success?next=${nextUrl}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    if (error) setError(null);
    setLoading(true);
    setUser(null);
    deleteCookieAuth();
    window.location.replace("/login");
  }

  React.useEffect(() => {
    setLoading(true);

    if (cookieAuth) {
      const localUser = JSON.parse(cookieAuth) as ILocalUser;
      const exp = jwtDecode(localUser.token ?? "").exp;

      exp && console.log("exp", new Date(exp * 1000));

      if (exp && exp < Date.now() / 1000) {
        logout();
        return;
      }

      if (nextUrl) navigate(nextUrl, { replace: true });
      else if (location.pathname === "/") {
        const appLink = appLinks.find(({ roles }) =>
          roles.includes(localUser.accessLevel)
        );
        if (appLink?.link)
          navigate(nextUrl ?? `${appLink.link}`, { replace: true });
      }

      setUser(localUser);
    }

    setLoading(false);
  }, [appLinks, cookieAuth, location.pathname, navigate, nextUrl]);

  useEffect(() => {
    async function getUser(userId: string) {
      const user = await services.user.getUserById(userId);
      if (user) {
        setUser((oldUser) => ({ ...oldUser, ...user }));
      }
    }
    if (userId) {
      getUser(userId);
    }
  }, [userId]);

  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        loading,
        login,
        signUp,
        logout,
        setLoading,
        setError,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthContextProvider };
